import React, { useEffect, useRef, useState } from 'react';
import './Account.css';
import PageLayout from '../PageLayout/PageLayout';
import { EmptyState } from '../EmptyState/EmptyState';
import { useAccount, usePortalSession } from '../../hooks/useAccount';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ApiError, CatalogEntry } from '../../services/reportsApi';
import { parseError } from '../../utils/errorHandlers';
import {
  isPaddleConfigured,
  openCheckout,
  setCheckoutEventHandler,
} from '../../utils/paddleLoader';
import { getPaddleConfig } from '../../utils/amplifyConfig';
import { formatDate } from '../../utils/formatters';
import { purchaseOptions } from '../../utils/purchaseOptions';
import { trackPurchase } from '../../utils/analytics';

// Balances above this render as "999+".
//
// Migrated alpha accounts can carry enormous balances -- the dev rehearsal
// found one at 49,892 runs -- and rendering that reads as a bug rather than as
// generosity. Display only; the stored balance is untouched and spends in full.
const DISPLAY_CAP = 999;

const formatRuns = (value: number): string =>
  value > DISPLAY_CAP ? `${DISPLAY_CAP}+` : String(value);

const SOURCE_LABELS: Record<string, string> = {
  subscription: 'Plan runs',
  purchase: 'Purchased',
  promo: 'Promotional',
  beta: 'Beta',
  unknown: 'Other',
};

// Already a literal in template.yaml (SENDGRID_FROM_EMAIL) and the
// CognitoReplyToEmail value in both environments. Not an env var: a new
// REACT_APP_* would have to be a stack output, and update-env-from-sam.sh
// truncates the env file on every run, so that is a liability for a static
// string. Task 22 replaces the mailto with a real plan-change control.
const SUPPORT_EMAIL = 'grow@gopropwise.com';

/**
 * Portal-specific error copy.
 *
 * The shared 404 message is report-centric ("Report not found.") and the
 * shared 403 says "Session expired.", both nonsense on this route, so the
 * codes the backend sends get their own wording and everything else falls
 * through to the generic parser.
 */
const portalErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError && err.code === 'NO_CUSTOMER') {
    return "We couldn't find a subscription to manage on this account.";
  }
  if (err instanceof ApiError && err.code === 'PORTAL_UNAVAILABLE') {
    return 'Subscription management is not available on this environment yet.';
  }
  if (err instanceof ApiError && err.code === 'PORTAL_FORBIDDEN') {
    // The billing record's email is not this account's. The honest cause is
    // usually a checkout under a different email, which support can fix.
    return (
      'This subscription is billed to a different email address than the one ' +
      `you signed in with. Contact ${SUPPORT_EMAIL} and we'll sort it out.`
    );
  }
  return parseError(err).userMessage;
};

const Account: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const {
    data: summary,
    isLoading,
    error,
    refetch,
    polling,
    startBalancePoll,
  } = useAccount();

  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const portal = usePortalSession();

  // GA4's purchase event names items by their catalog label (task 26). Read
  // through a ref so the checkout handler sees the current catalog without
  // being re-registered mid-checkout.
  const catalogRef = useRef<CatalogEntry[]>([]);
  catalogRef.current = summary?.catalog ?? [];

  // The overlay closes before the balance changes: the provider notifies the
  // backend out of band, so the browser is never told directly. Polling is how
  // the page finds out, and it is bounded because a balance has no terminal
  // state to stop on.
  useEffect(() => {
    setCheckoutEventHandler((event) => {
      if (event.name === 'checkout.completed') {
        setPendingPriceId(null);
        showSuccess('Payment received. Updating your balance…');
        startBalancePoll();
        // After the existing handling, and it never throws (task 26).
        trackPurchase(event.data, catalogRef.current);
      }
      if (event.name === 'checkout.closed') {
        setPendingPriceId(null);
      }
    });
    return () => setCheckoutEventHandler(null);
  }, [startBalancePoll, showSuccess]);

  const handleBuy = async (entry: CatalogEntry) => {
    if (!user) {
      return;
    }
    setPendingPriceId(entry.priceId);
    try {
      const config = getPaddleConfig();
      await openCheckout({
        priceId: entry.priceId,
        email: user.email,
        // The Cognito sub. The backend treats it as a routing hint and falls
        // back to a lookup if it is missing or malformed, so this is a
        // convenience rather than the thing that authorises the credit.
        userId: user.id,
        // Only when this account is eligible AND a discount is configured.
        // What the overlay does with an ineligible discount is undocumented,
        // so the decision stays on our side.
        discountId:
          summary?.betaEligible && config?.betaDiscountId
            ? config.betaDiscountId
            : undefined,
      });
    } catch (err) {
      setPendingPriceId(null);
      showError(parseError(err).userMessage);
    }
  };

  const handleManage = async () => {
    try {
      const { url } = await portal.mutateAsync();
      // A full-page navigation, not a router push: the destination is the
      // provider's own domain, so a push would not leave the app.
      window.location.assign(url);
    } catch (err) {
      showError(portalErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <PageLayout title="Account" subtitle="Your plan and inspection runs">
        <div className="account-loading" role="status">Loading your account…</div>
      </PageLayout>
    );
  }

  if (error || !summary) {
    const appError = parseError(error);
    return (
      <PageLayout title="Account" subtitle="Your plan and inspection runs">
        <EmptyState
          icon="bi bi-exclamation-triangle"
          iconColor="red"
          title="Couldn't load your account"
          message={appError.userMessage}
          actions={appError.retryable ? [{ label: 'Try Again', onClick: () => refetch() }] : []}
        />
      </PageLayout>
    );
  }

  // Two figures, never one. They expire on different schedules and are spent
  // in a defined order -- subscription runs first -- so a combined total would
  // be a number nobody can act on.
  const statsCards = (
    <div className="header-stats">
      <div className="stat-card" data-testid="subscription-runs">
        <div className="stat-value">{formatRuns(summary.subscriptionRuns)}</div>
        <div className="stat-label">Plan runs</div>
      </div>
      <div className="stat-card" data-testid="purchased-credits">
        <div className="stat-value">{formatRuns(summary.purchasedCredits)}</div>
        {/* "Credits", not "Purchased credits". The backend field groups every
            non-subscription grant here -- purchases, promos and beta grants
            alike -- because they share a spend order and only subscription
            runs are treated differently. Calling that "purchased" is wrong on
            screen for a beta user who bought nothing, which is most of the
            people looking at this page right now. The expiry list below names
            each grant's real source, so nothing is lost by the shorter word. */}
        <div className="stat-label">Credits</div>
      </div>
    </div>
  );

  // Plans are hidden once a subscription exists; see purchaseOptions for why
  // that is the only enforcement of "one Core subscription at a time".
  //
  // renewalOnly: a subscriber with no one-time packs on the allowlist is not
  // the same as an environment where nothing is purchasable. Without it, the
  // page would tell someone who is actively paying that "purchasing isn't
  // available yet".
  const { packs, plans, canPurchase, renewalOnly } = purchaseOptions(summary);

  return (
    <PageLayout
      title="Account"
      subtitle="Your plan and inspection runs"
      headerActions={statsCards}
    >
      <section className="account-section">
        <h2 className="account-section-title">Plan</h2>
        <p className="account-plan" data-testid="plan-name">{summary.planName}</p>
        {polling && (
          <p className="account-polling" role="status">
            Waiting for your purchase to land…
          </p>
        )}
        {summary.hasActiveSubscription && (
          <div className="account-plan-actions">
            {summary.subscriptionCancelsAt && (
              // Cancelling at period end leaves the subscription active, so
              // this line is the only thing that changes. Without it the page
              // is identical before and after a cancellation and the user
              // quite reasonably cancels again.
              <p className="account-plan-cancelling" data-testid="plan-cancels-at">
                Cancels on {formatDate(summary.subscriptionCancelsAt)}. Your runs
                stay usable until then.
              </p>
            )}
            <button
              type="button"
              className="btn btn-secondary account-manage"
              onClick={handleManage}
              disabled={portal.isPending}
            >
              {portal.isPending ? 'Opening…' : 'Manage subscription'}
            </button>
            <p className="account-plan-note">
              Need a different plan?{' '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Change%20my%20PropWise%20plan`}>
                Contact us to change plan
              </a>
              .
            </p>
          </div>
        )}
      </section>

      {summary.expiries.length > 0 && (
        <section className="account-section">
          <h2 className="account-section-title">When your runs expire</h2>
          <ul className="account-expiries">
            {summary.expiries.map((expiry, index) => (
              <li key={`${expiry.expiresAt ?? 'never'}-${expiry.source}-${index}`}>
                <span className="account-expiry-runs">{formatRuns(expiry.runs)}</span>
                <span className="account-expiry-source">
                  {SOURCE_LABELS[expiry.source] ?? SOURCE_LABELS.unknown}
                </span>
                <span className="account-expiry-date">
                  {expiry.expiresAt ? formatDate(expiry.expiresAt) : 'No expiry'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {plans.length > 0 && isPaddleConfigured() && (
        <section className="account-section">
          <h2 className="account-section-title">Choose a plan</h2>
          <p className="account-section-note">
            A monthly subscription with a run allowance that renews each billing
            period. Unused plan runs do not carry over.
          </p>
          <div className="account-catalog">
            {plans.map((entry) => (
              <button
                key={entry.priceId}
                type="button"
                className="btn btn-primary account-buy"
                onClick={() => handleBuy(entry)}
                disabled={pendingPriceId !== null}
              >
                {pendingPriceId === entry.priceId ? 'Opening checkout…' : entry.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {!renewalOnly && (
        <section className="account-section">
          <h2 className="account-section-title">Buy more runs</h2>
          {canPurchase ? (
            <div className="account-catalog">
              {packs.map((entry) => (
                <button
                  key={entry.priceId}
                  type="button"
                  className="btn btn-primary account-buy"
                  onClick={() => handleBuy(entry)}
                  disabled={pendingPriceId !== null}
                >
                  {pendingPriceId === entry.priceId ? 'Opening checkout…' : entry.label}
                </button>
              ))}
            </div>
          ) : (
            // An empty catalog is a real state, not an error: prod carries no
            // purchasable prices between the entitlement cutover and the live
            // move. Saying so is better than an empty space where buttons
            // should be, and better than the denial message promising a
            // purchase path that does not exist yet.
            //
            // A paying subscriber never reaches this copy: renewalOnly
            // drops the whole section for them.
            <EmptyState
              icon="bi bi-cart"
              iconColor="muted"
              size="inline"
              message="Purchasing isn't available yet. Contact PropWise to add runs to your account."
            />
          )}
        </section>
      )}
    </PageLayout>
  );
};

export default Account;
